import React, { useState, useEffect } from "react";
import { Plus, X, Save } from "lucide-react";
import { useContext } from "react";
import { AccountsContext } from "../context/AccountsContext";

//For user backend data storage

const TOKEN_KEY = "ct_login_token";
const USER_KEY = "ct_login_user";

function getLoginUser() {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY) || "null");
  } catch {
    return null;
  }
}

function getGroupsStorageKey() {
  const user = getLoginUser();
  return user?.id ? `ct_groups_${user.id}` : "ct_groups_guest";
}


// ✅ Default form outside component (stable reference)
const emptyForm = {
  name: "",
  parent: "",
  children: [],
  segments: ["NSE", "BSE", "MCX", "NFO", "BFO", "CDS"],
  orderTypes: ["OPEN", "COMPLETE", "CANCELLED", "REJECTED"],
  direction: "Parallel",
  forceMarket: false,
};

const ALL_SEGMENTS = ["NSE", "BSE", "MCX", "NFO", "BFO", "CDS"];
const ALL_ORDER_TYPES = ["OPEN", "COMPLETE", "CANCELLED", "REJECTED"];
const COPY_MODES = ["Multiplier", "Fixed Quantity", "Fixed Lot"];

// ── ADDITION 1: Meta for colored order status icons ──────────
const ORDER_TYPE_META = {
  OPEN:      { icon: "🟢", color: "text-green-400" },
  COMPLETE:  { icon: "🔵", color: "text-blue-400"  },
  CANCELLED: { icon: "⊗",  color: "text-gray-400"  },
  REJECTED:  { icon: "🔴", color: "text-red-400"   },
};

const DIRECTION_OPTIONS = [
  { value: "Parallel", label: "Parallel (BUY→BUY, SELL→SELL)" },
  { value: "Reverse",  label: "Reverse (BUY→SELL, SELL→BUY)"  },
];
// ─────────────────────────────────────────────────────────────

// ── Sync groups to backend copy engine ───────────────────────
async function syncGroupsToBackend() {
  try {
    const ctGroups   = JSON.parse(localStorage.getItem("ct_groups")   || "[]");
    const ctAccounts = JSON.parse(localStorage.getItem("ct_accounts") || "[]");
    await fetch("/api/copy/groups/sync", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ctGroups, ctAccounts }),
    });
    console.log("✅ Groups synced to backend");
  } catch (e) {
    console.log("⚠ Group sync failed:", e.message);
  }
}
// ─────────────────────────────────────────────────────────────

export default function Groups() {
  const { accounts } = useContext(AccountsContext);
  const loginToken = localStorage.getItem(TOKEN_KEY);
  const loginUser = getLoginUser();
  const groupsStorageKey = getGroupsStorageKey();
  const isAdmin = loginUser?.role === "admin";



  // ✅ FIX: Load groups from localStorage on first render
  const [groups, setGroups] = useState([]);
  const [groupsLoaded, setGroupsLoaded] = useState(false);


  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });

  // ✅ FIX: Save groups to localStorage whenever they change
  useEffect(() => {
  if (!groupsLoaded) return;

  const groupsToSave = isAdmin
  ? groups
  : groups.map(g => ({
      ...g,
      ownerUserId: loginUser?.id,
      ownerUsername: loginUser?.username,
    }));

localStorage.setItem(groupsStorageKey, JSON.stringify(groupsToSave));
localStorage.setItem("ct_groups", JSON.stringify(groupsToSave));


  if (loginToken) {
    fetch("/api/login/data/groups", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${loginToken}`,
      },
      body: JSON.stringify({ groups: groupsToSave }),
    }).catch(err => console.log("Group save failed:", err.message));
  }

  syncGroupsToBackend();
}, [groups, groupsLoaded]);



//For user backend data storage
useEffect(() => {
  if (!loginToken) {
    setGroups([]);
    setGroupsLoaded(true);
    return;
  }

  fetch("/api/login/data/groups", {
    headers: { Authorization: `Bearer ${loginToken}` },
  })
    .then(res => res.json())
    .then(data => {
      const rawGroups = data.groups || [];
const loaded = isAdmin
  ? rawGroups
  : rawGroups.filter(g => String(g.ownerUserId || "") === String(loginUser?.id));

setGroups(loaded);

      localStorage.setItem(groupsStorageKey, JSON.stringify(loaded));
      localStorage.setItem("ct_groups", JSON.stringify(loaded));
      setGroupsLoaded(true);
      setTimeout(syncGroupsToBackend, 100);
    })
    .catch(err => {
      console.log("Group load failed:", err.message);
      setGroups([]);
      setGroupsLoaded(true);
    });
}, []);



  // ── View Members Modal state ──────────────────────────────
  const [membersGroup, setMembersGroup] = useState(null);
  const [showMembersModal, setShowMembersModal] = useState(false);

  const openMembers = (group) => {
    setMembersGroup(group);
    setShowMembersModal(true);
  };

  const closeMembers = () => {
    setMembersGroup(null);
    setShowMembersModal(false);
  };

  // ── ADDITION 2: Config modal state ───────────────────────
  const [configGroup,     setConfigGroup]     = useState(null);
  const [configForm,      setConfigForm]      = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);

  const openConfig = (g) => {
    setConfigGroup(g);
    setConfigForm({
      segments:    [...g.segments],
      orderTypes:  [...g.orderTypes],
      direction:   g.direction,
      forceMarket: g.forceMarket,
    });
    setShowConfigModal(true);
  };

  const closeConfig = () => {
    setConfigGroup(null);
    setConfigForm(null);
    setShowConfigModal(false);
  };

  const saveConfig = () => {
    setGroups(prev =>
      prev.map(g => g.id !== configGroup.id ? g : { ...g, ...configForm })
    );
    closeConfig();
    setTimeout(syncGroupsToBackend, 100);
  };

  const toggleConfigSegment = (seg) => {
    setConfigForm(prev => ({
      ...prev,
      segments: prev.segments.includes(seg)
        ? prev.segments.filter(s => s !== seg)
        : [...prev.segments, seg],
    }));
  };

  const toggleConfigOrderType = (type) => {
    setConfigForm(prev => ({
      ...prev,
      orderTypes: prev.orderTypes.includes(type)
        ? prev.orderTypes.filter(t => t !== type)
        : [...prev.orderTypes, type],
    }));
  };
  // ─────────────────────────────────────────────────────────

  // ── Member helpers ────────────────────────────────────────

  // Add a child account to an existing group (from members modal)
  const addMemberToGroup = (groupId, childId) => {
    if (!childId) return;
    setGroups(prev =>
      prev.map(g => {
        if (g.id !== groupId) return g;
        if (g.children.find(c => c.id === childId)) return g; // already exists
        const acc = accounts.find(a => a.id === childId);
        const newMember = {
          id: childId,
          name: acc ? acc.name : childId,
          broker: acc ? acc.broker : "Dhan",
          mode: "Multiplier",
          value: 1,
          active: true,
        };
        return { ...g, children: [...g.children, newMember] };
      })
    );
    // keep membersGroup in sync
    setMembersGroup(prev => {
      if (!prev || prev.id !== groupId) return prev;
      if (prev.children.find(c => c.id === childId)) return prev;
      const acc = accounts.find(a => a.id === childId);
      return {
        ...prev,
        children: [
          ...prev.children,
          {
            id: childId,
            name: acc ? acc.name : childId,
            broker: acc ? acc.broker : "Dhan",
            mode: "Multiplier",
            value: 1,
            active: true,
          },
        ],
      };
    });
  };

  // Update a member's field (mode, value, active)
  const updateMember = (groupId, childId, field, value) => {
    setGroups(prev =>
      prev.map(g =>
        g.id !== groupId
          ? g
          : {
              ...g,
              children: g.children.map(c =>
                c.id !== childId ? c : { ...c, [field]: value }
              ),
            }
      )
    );
    setMembersGroup(prev =>
      !prev || prev.id !== groupId
        ? prev
        : {
            ...prev,
            children: prev.children.map(c =>
              c.id !== childId ? c : { ...c, [field]: value }
            ),
          }
    );
  };

  // Remove a member from a group
  const removeMember = (groupId, childId) => {
    setGroups(prev =>
      prev.map(g =>
        g.id !== groupId
          ? g
          : { ...g, children: g.children.filter(c => c.id !== childId) }
      )
    );
    setMembersGroup(prev =>
      !prev || prev.id !== groupId
        ? prev
        : { ...prev, children: prev.children.filter(c => c.id !== childId) }
    );
  };

  // ── Create Group form helpers ─────────────────────────────

  const addChild = (childId) => {
    if (!childId || form.children.find(c => c.id === childId)) return;
    const acc = accounts.find(a => a.id === childId);
    setForm(prev => ({
      ...prev,
      children: [
        ...prev.children,
        {
          id: childId,
          name: acc ? acc.name : childId,
          broker: acc ? acc.broker : "Dhan",
          mode: "Multiplier",
          value: 1,
          active: true,
        },
      ],
    }));
  };

  const removeChild = (childId) => {
    setForm(prev => ({
      ...prev,
      children: prev.children.filter(c => c.id !== childId),
    }));
  };

  const toggleSegment = (seg) => {
    setForm(prev => ({
      ...prev,
      segments: prev.segments.includes(seg)
        ? prev.segments.filter(s => s !== seg)
        : [...prev.segments, seg],
    }));
  };

  const toggleOrderType = (type) => {
    setForm(prev => ({
      ...prev,
      orderTypes: prev.orderTypes.includes(type)
        ? prev.orderTypes.filter(t => t !== type)
        : [...prev.orderTypes, type],
    }));
  };

  const createGroup = () => {
    if (!form.name || !form.parent) {
      alert("Group name and parent account required");
      return;
    }
    const newGroup = {
      ownerUserId: loginUser?.id,
      ownerUsername: loginUser?.username,
      id: Date.now(),
      ...form,
      active: true,
    };
    setGroups(prev => [...prev, newGroup]);
    setForm({ ...emptyForm });
    setShowModal(false);
    setTimeout(syncGroupsToBackend, 100); // sync after state updates
  };

  // ── ADDITION 3: Exclude accounts already used in any existing group ──
  const usedAccountIds = new Set(
    groups.flatMap(g => [g.parent, ...g.children.map(c => c.id)])
  );
  const availableChildren = accounts.filter(
    a => a.id !== form.parent && !usedAccountIds.has(a.id)
  );
  // Also exclude the currently-selected parent from used set for parent dropdown
  // (parent dropdown still shows all accounts so user can pick any as parent)
  // ─────────────────────────────────────────────────────────────────────

  // Accounts not yet in this group (for Add Member dropdown)
  const availableToAdd = (group) =>
    accounts.filter(
      a => a.id !== group.parent && !group.children.find(c => c.id === a.id)
    );

  return (
    <div className="mobile-page mobile-groups p-6">

      {/* HEADER */}
      <div className="flex justify-between mb-6">
        <h1 className="text-3xl font-bold">Copy Trading Groups</h1>
        <button
          onClick={() => { setForm({ ...emptyForm }); setShowModal(true); }}
          className="bg-blue-500 px-4 py-2 rounded flex gap-2 items-center"
        >
          <Plus size={16} /> Create Group
        </button>
      </div>

      {/* GROUP TABLE */}
      <div className="mobile-grid-table bg-[#1e293b] rounded-xl overflow-hidden">
        <div className="mobile-table-head grid grid-cols-6 p-4 text-gray-400 border-b border-gray-700 text-sm font-semibold uppercase tracking-wide">
          <span>Group Name</span>
          <span>Parent Account</span>
          <span>Children</span>
          <span>Config</span>
          <span>Active</span>
          <span>Actions</span>
        </div>

        {groups.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            No groups created yet. Click "Create Group" to get started.
          </div>
        )}

        {groups.map(g => (
          <div key={g.id} className="mobile-card-row grid grid-cols-6 p-4 border-b border-gray-800 items-center">

            <span className="font-semibold">{g.name}</span>

            <span className="text-gray-300">
              {accounts.find(a => a.id === g.parent)?.name || g.parent}
              <span className="text-gray-500 text-xs ml-1">({g.parent})</span>
            </span>

            <span
              className="text-blue-400 cursor-pointer hover:text-blue-300 transition-colors"
              onClick={() => openMembers(g)}
            >
              👥 View Members ({g.children.length})
            </span>

            <span className="bg-gray-700 px-2 py-1 rounded text-sm w-fit">
              {g.segments.length === 6 ? "All Segments" : `${g.segments.length} Segments`}
            </span>

            <span>
              <button
                onClick={() =>
                  setGroups(prev =>
                    prev.map(item =>
                      item.id === g.id ? { ...item, active: !item.active } : item
                    )
                  )
                }
                className={`w-12 h-6 rounded-full transition-colors ${g.active ? "bg-blue-500" : "bg-gray-500"}`}
              />
            </span>

            <span className="flex gap-3 text-lg">
              <span className="cursor-pointer text-blue-400" onClick={() => openMembers(g)}>👥</span>
              {/* ADDITION 2: ⚙️ now opens config modal */}
              <span className="cursor-pointer text-purple-400" onClick={() => openConfig(g)}>⚙️</span>
              <span className="cursor-pointer text-green-400">⚖️</span>
              <span
                className="cursor-pointer text-red-500"
                onClick={() => { setGroups(prev => prev.filter(item => item.id !== g.id)); setTimeout(syncGroupsToBackend, 100); }}
              >
                🗑️
              </span>
            </span>

          </div>
        ))}
      </div>

      {/* ── ADDITION 2: EDIT CONFIG MODAL ────────────────────── */}
      {showConfigModal && configGroup && configForm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50">
          <div className="bg-[#1e293b] rounded-xl w-[560px] max-h-[90vh] flex flex-col overflow-hidden">

            {/* Header */}
            <div className="flex justify-between items-start p-5 border-b border-gray-700">
              <div>
                <h2 className="text-xl font-bold">Edit Group Configuration</h2>
                <p className="text-gray-400 text-sm mt-0.5">
                  Group: <span className="text-white">{configGroup.name}</span>
                </p>
              </div>
              <X className="cursor-pointer text-gray-400 hover:text-white mt-1" onClick={closeConfig} />
            </div>

            <div className="overflow-y-auto flex-1 p-5 flex flex-col gap-4">

              {/* ALLOWED SEGMENTS */}
              <div className="bg-[#243447] border border-gray-700 rounded-lg p-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <span>▼</span> Allowed Segments (Exchanges)
                </h3>
                <div className="grid grid-cols-3 gap-y-3 gap-x-4">
                  {ALL_SEGMENTS.map(seg => (
                    <label key={seg} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="accent-blue-500 w-4 h-4"
                        checked={configForm.segments.includes(seg)}
                        onChange={() => toggleConfigSegment(seg)}
                      />
                      <span className="text-sm">{seg}</span>
                    </label>
                  ))}
                </div>
                <p className="text-gray-500 text-xs mt-3 flex items-center gap-1">
                  ℹ️ All checked = Copy all segments
                </p>
              </div>

              {/* ALLOWED ORDER STATUSES */}
              <div className="bg-[#243447] border border-gray-700 rounded-lg p-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <span>≡</span> Allowed Order Statuses
                </h3>
                <div className="grid grid-cols-2 gap-y-3 gap-x-4">
                  {ALL_ORDER_TYPES.map(type => (
                    <label key={type} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="accent-blue-500 w-4 h-4"
                        checked={configForm.orderTypes.includes(type)}
                        onChange={() => toggleConfigOrderType(type)}
                      />
                      <span className="text-sm">
                        {ORDER_TYPE_META[type].icon}{" "}
                        <span className={ORDER_TYPE_META[type].color + " font-medium"}>{type}</span>
                      </span>
                    </label>
                  ))}
                </div>
                <p className="text-gray-500 text-xs mt-3 flex items-center gap-1">
                  ℹ️ All checked = Copy all statuses
                </p>
              </div>

              {/* COPY DIRECTION */}
              <div className="bg-[#243447] border border-gray-700 rounded-lg p-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <span>⇄</span> Copy Direction
                </h3>
                <select
                  className="w-full p-2.5 rounded bg-gray-700 border border-gray-600 text-sm"
                  value={configForm.direction}
                  onChange={(e) => setConfigForm(prev => ({ ...prev, direction: e.target.value }))}
                >
                  {DIRECTION_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* FORCE MARKET ORDERS */}
              <div className="bg-[#243447] border border-gray-700 rounded-lg p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="accent-blue-500 w-4 h-4 mt-0.5"
                    checked={configForm.forceMarket}
                    onChange={(e) => setConfigForm(prev => ({ ...prev, forceMarket: e.target.checked }))}
                  />
                  <div>
                    <p className="font-semibold text-sm">⚡ Force Market Orders (Immediate Execution)</p>
                    <p className="text-gray-500 text-xs mt-0.5">LIMIT → MARKET | SL-L → SL-M</p>
                  </div>
                </label>
              </div>

            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 p-4 border-t border-gray-700">
              <button
                onClick={closeConfig}
                className="px-5 py-2 bg-gray-600 hover:bg-gray-500 rounded text-sm transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveConfig}
                className="px-5 py-2 bg-blue-500 hover:bg-blue-400 rounded text-sm transition-colors flex items-center gap-2"
              >
                <Save size={14} /> Save Configuration
              </button>
            </div>

          </div>
        </div>
      )}
      {/* ─────────────────────────────────────────────────────── */}

      {/* ── VIEW MEMBERS MODAL ───────────────────────────────── */}
      {showMembersModal && membersGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50">
          <div className="bg-[#1e293b] rounded-xl w-[700px] max-h-[80vh] flex flex-col">

            {/* Header */}
            <div className="flex justify-between items-start p-5 border-b border-gray-700">
              <div>
                <h2 className="text-xl font-bold">Group Members</h2>
                <p className="text-gray-400 text-sm mt-1">
                  Group: <span className="text-white">{membersGroup.name}</span>
                  {" | "}Parent:{" "}
                  <span className="text-white">
                    {accounts.find(a => a.id === membersGroup.parent)?.name || membersGroup.parent}
                    {" "}({membersGroup.parent})
                  </span>
                </p>
              </div>
              <X className="cursor-pointer text-gray-400 hover:text-white mt-1" onClick={closeMembers} />
            </div>

            {/* Add Member */}
            <div className="px-5 pt-4">
              <select
                className="text-blue-400 bg-transparent border border-blue-500 rounded px-3 py-1 text-sm cursor-pointer hover:bg-blue-500 hover:text-white transition-colors"
                defaultValue=""
                onChange={(e) => {
                  addMemberToGroup(membersGroup.id, e.target.value);
                  e.target.value = "";
                }}
              >
                <option value="" disabled>+ Add Member</option>
                {availableToAdd(membersGroup).map(acc => (
                  <option key={acc.id} value={acc.id}>
                    {acc.name} ({acc.id})
                  </option>
                ))}
              </select>
            </div>

            {/* Members Table */}
            <div className="overflow-y-auto flex-1 px-5 py-3">

              {/* Table Header */}
              <div className="grid grid-cols-6 text-gray-400 text-sm font-semibold uppercase tracking-wide pb-2 border-b border-gray-700">
                <span>Account</span>
                <span>User ID</span>
                <span>Broker</span>
                <span>Mode</span>
                <span>Value</span>
                <span className="text-center">Status</span>
              </div>

              {membersGroup.children.length === 0 && (
                <div className="py-8 text-center text-gray-500 text-sm">
                  No members yet. Click "+ Add Member" above.
                </div>
              )}

              {membersGroup.children.map(child => (
                <div
                  key={child.id}
                  className="grid items-center py-3 border-b border-gray-800 gap-2"
                  style={{ gridTemplateColumns: "1fr 1.2fr 0.8fr 1.4fr 0.7fr 0.6fr 0.4fr" }}
                >
                  {/* Account Name */}
                  <span className="font-medium text-white">{child.name || child.id}</span>

                  {/* User ID */}
                  <span className="text-gray-300 text-sm">{child.id}</span>

                  {/* Broker */}
                  <span className="text-gray-300 text-sm uppercase">{child.broker}</span>

                  {/* Mode dropdown */}
                  <select
                    className="bg-gray-700 text-white text-sm rounded px-2 py-1 border border-gray-600"
                    value={child.mode}
                    onChange={(e) =>
                      updateMember(membersGroup.id, child.id, "mode", e.target.value)
                    }
                  >
                    {COPY_MODES.map(m => (
                      <option key={m}>{m}</option>
                    ))}
                  </select>

                  {/* Value input */}
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    className="bg-gray-700 text-white text-sm rounded px-2 py-1 border border-gray-600 w-16"
                    value={child.value}
                    onChange={(e) =>
                      updateMember(membersGroup.id, child.id, "value", e.target.value)
                    }
                  />

                  {/* Status toggle */}
                  <div className="flex justify-center">
                    <button
                      onClick={() =>
                        updateMember(membersGroup.id, child.id, "active", !child.active)
                      }
                      className={`w-11 h-6 rounded-full relative transition-colors duration-200 ${
                        child.active ? "bg-green-500" : "bg-gray-500"
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                          child.active ? "translate-x-5" : "translate-x-0.5"
                        }`}
                      />
                    </button>
                  </div>

                  {/* Delete */}
                  <div className="flex justify-center">
                    <button
                      onClick={() => removeMember(membersGroup.id, child.id)}
                      className="text-red-500 hover:text-red-400 transition-colors text-lg"
                      title="Remove member"
                    >
                      🗑️
                    </button>
                  </div>

                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="flex justify-end p-4 border-t border-gray-700">
              <button
                onClick={closeMembers}
                className="px-5 py-2 bg-gray-600 hover:bg-gray-500 rounded text-sm transition-colors"
              >
                Close
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ── CREATE GROUP MODAL ───────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-[#1e293b] p-6 rounded-xl w-[500px] max-h-[90vh] overflow-y-auto">

            <div className="flex justify-between mb-4">
              <h2 className="text-xl font-semibold">Create Copy Group</h2>
              <X onClick={() => setShowModal(false)} className="cursor-pointer" />
            </div>

            <div className="flex flex-col gap-3">

              {/* GROUP NAME */}
              <input
                placeholder="Group Name"
                className="p-2 rounded bg-gray-700"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />

              {/* PARENT ACCOUNT */}
              <select
                className="p-2 rounded bg-gray-700"
                value={form.parent}
                onChange={(e) =>
                  setForm({ ...form, parent: e.target.value, children: [] })
                }
              >
                <option value="">Select Parent Account</option>
                {accounts.map(acc => (
                  <option key={acc.id} value={acc.id}>
                    {acc.name} ({acc.id})
                  </option>
                ))}
              </select>

              {/* CHILD ACCOUNTS */}
              <div className="mt-1">
                <h3 className="font-semibold mb-2">Child Accounts</h3>

                <select
                  className="p-2 rounded bg-gray-700 w-full"
                  onChange={(e) => { addChild(e.target.value); e.target.value = ""; }}
                  defaultValue=""
                >
                  <option value="" disabled>+ Add Child Account</option>
                  {availableChildren.map(acc => (
                    <option key={acc.id} value={acc.id}>
                      {acc.name} ({acc.id})
                    </option>
                  ))}
                </select>

                <div className="mt-2 space-y-2">
                  {form.children.length === 0 && (
                    <p className="text-gray-400 text-sm">No children added yet</p>
                  )}
                  {form.children.map(child => (
                    <div key={child.id} className="flex justify-between bg-gray-800 p-2 rounded items-center">
                      <span className="text-sm">{child.name} ({child.id})</span>
                      <button className="text-red-400 text-sm" onClick={() => removeChild(child.id)}>
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* SEGMENTS */}
              <div className="bg-gray-800 p-3 rounded mt-1">
                <h3 className="mb-2 font-semibold">Allowed Segments</h3>
                <div className="grid grid-cols-3 gap-2">
                  {ALL_SEGMENTS.map(seg => (
                    <label key={seg} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.segments.includes(seg)}
                        onChange={() => toggleSegment(seg)}
                      />
                      {seg}
                    </label>
                  ))}
                </div>
              </div>

              {/* ORDER STATUS */}
              <div className="bg-gray-800 p-3 rounded">
                <h3 className="mb-2 font-semibold">Allowed Order Status</h3>
                <div className="grid grid-cols-2 gap-2">
                  {ALL_ORDER_TYPES.map(type => (
                    <label key={type} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.orderTypes.includes(type)}
                        onChange={() => toggleOrderType(type)}
                      />
                      {type}
                    </label>
                  ))}
                </div>
              </div>

              {/* COPY DIRECTION */}
              <select
                className="p-2 rounded bg-gray-700"
                value={form.direction}
                onChange={(e) => setForm({ ...form, direction: e.target.value })}
              >
                <option>Parallel</option>
                <option>Reverse</option>
              </select>

              {/* FORCE MARKET */}
              <label className="flex gap-2 items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.forceMarket}
                  onChange={(e) => setForm({ ...form, forceMarket: e.target.checked })}
                />
                Force Market Orders
              </label>

            </div>

            <div className="flex justify-end gap-3 mt-5">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 bg-gray-600 rounded"
              >
                Cancel
              </button>
              <button
                onClick={createGroup}
                className="px-4 py-2 bg-blue-500 rounded"
              >
                Create Group
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}